import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { CsPage, NavController } from 'providers/nav-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { TelemetryService } from 'services/telemetry.service';
import { FoodDbService } from 'services/food-db.service';
import { FoodReadFailurePolicy } from 'services/food-read-error';
import { ReportedSearchFailureError } from 'services/food-search-error';
import { ConfigDbService } from 'services/config-db.service';
import { DebugService } from 'services/debug.service';
import { SettingsService } from 'services/settings.service';
import { CameraInitResult } from 'services/camera.service';
import { LoggingService } from 'services/logging.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { runScanFailureRecoveryAsync } from 'services/scan-error-policy';
import { showScanRetryAlertAsync } from 'services/scan-error-alert';
import { UserDb } from 'services/user-db.service';
import { WakeLockService } from 'services/wake-lock.service';
import { ScanMode } from 'services/scan-config';
import { matchScannedFoodForScannerAsync, scannerFoodHeader } from 'services/scanner-food-match';
import { describeError } from '@cf-shared/error-describe';
import { StapleDbService } from 'services/staple-db.service';
import { ScannerModelSelection } from 'services/scanner-model-selection';
import { ScannerPreferenceMemory } from 'services/scanner-preference-memory';
import { FakeScanProgress } from 'services/scan-progress';
import { MultiModelFallbackManager, runMultiModelScanAsync } from 'services/multi-model-fallback';
import { openScannerImagePicker } from 'services/scanner-file-picker';
import {
  CameraStopOrdering,
  ScannerPageAnimationHooks,
  animateCapturedPhotoToReelAsync,
  animateCollageTransitionAsync,
} from 'services/scanner-capture-animations';
import { ZoomImagePopover } from 'components/zoom-image-popover/zoom-image-popover';
import { PopoverController } from 'providers/popover-controller';
import { OnlineStateService } from 'services/online-state.service';
import { AnimationController } from 'providers/animation-controller';
import { CsCameraViewComponent } from 'components/cs-camera-view/cs-camera-view';
import { CsScannerCameraStageComponent } from 'components/cs-scanner-camera-stage/cs-scanner-camera-stage';
import { CsScannerPhotoReelComponent } from 'components/cs-scanner-photo-reel/cs-scanner-photo-reel';
import { ScanRecipeResults, RecipeScanIngredient, ScanRecipeError, ScanRecipeQuotaRejection, MAX_RECIPE_SCAN_IMAGES } from '@cf-shared/scan-recipe-interface';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { PopupMenuProvider } from 'providers/popup-menu/popup-menu';
import { MOCK_SCANNED_RECIPES } from './recipe-scan-mock-fixtures';
import { Food, FoodHeader } from '@cf-shared/data-model/food';
import { UiDisplayNutrient } from 'models/display-nutrient';
import { scannedRowDisplayNutrients } from 'services/scanned-row-nutrients';
import { IFoodsPage, FoodsPageResult } from 'pages/foods/foods-interface';
import { EditIngredientMode, IEditIngredientPage, EditIngredientResult, EditIngredientReturnCapability } from 'pages/edit-ingredient/edit-ingredient-interface';
import { typedWeight } from 'pages/edit-ingredient/weighed-with-refuse';
import { IngredientReal, IngredientType, isIngredientReal } from '@cf-shared/data-model/ingredient';
import { RecipeScannerPageParams, RecipeScannerResult, RecipeScannerMatchedIngredient, setPendingRecipeScanImage } from './recipe-scanner-interface';
import { findScannedRowById, mintScannedRowId } from 'services/scanner-row-identity';
import { ScannerName } from 'services/camera-permission-help';

interface ResultItem {
  // identifies this row. Minted per row, because aiName does NOT identify one: a
  // recipe can list the same ingredient twice -- olive oil for the pan and again for
  // the dressing -- so two rows carry one name, and keying off the name wrote one
  // row's edit onto the other. See services/scanner-row-identity.
  rowId: string;
  aiName: string;
  aiEstimatedGrams: number;  // original AI estimate (never changes)
  estimatedGrams: number;    // user-adjusted value (updated via edit ingredient)
  confidence: number;
  matched: boolean;
  checked: boolean;
  foodHeader: FoodHeader;    // matched DB food or placeholder
  food?: Food;               // the full matched food, loaded so the row's numbers can be scaled to its grams
  nutrients?: UiDisplayNutrient[];  // what the row's grams hold; empty when the food could not be loaded (#6806)
  subtitle: TranslatedString;
  foodId?: string;           // only set when matched
  weighedWithRefuse?: { percent: number };  // the "Weighed with peel" tick from the portion screen (#5544)
}

@Component({
  selector: 'page-recipe-scanner',
  templateUrl: 'recipe-scanner-page.html',
  host: { 'class': 'recipe-scanner cs-scanner-page' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RecipeScannerPage extends CsPage<RecipeScannerPageParams> implements AfterViewInit {
  protected ScannerName = ScannerName;
  protected LL = LL;
  protected MAX_RECIPE_SCAN_IMAGES = MAX_RECIPE_SCAN_IMAGES;

  @ViewChild('stage') stage: CsScannerCameraStageComponent;
  @ViewChild('reel') reel: CsScannerPhotoReelComponent;
  cameraPermissionDismissed = false;

  /** the wrapped camera view inside the shared camera stage */
  get cameraView(): CsCameraViewComponent | undefined {
    return this.stage?.cameraView;
  }

  phase: 'camera' | 'results' = 'camera';
  /** up to MAX_RECIPE_SCAN_IMAGES photos — a recipe can span several pages */
  capturedPhotos: { dataUrl: string; base64: string }[] = [];
  get hasPhotos(): boolean { return this.capturedPhotos.length > 0; }
  get allPhotosTaken(): boolean { return this.capturedPhotos.length >= MAX_RECIPE_SCAN_IMAGES; }
  analyzing = false;

  // extracted recipe header — editable name + the servings count the AI read
  recipeName = '';
  servings: number | undefined;
  // instruction steps the AI extracted — shown (and editable) on the results screen and
  // carried through to apply
  protected scannedInstructions = '';
  // 0-based index of the captured photo the AI flagged as the cooked-dish photo (the
  // recipe image), or undefined when no photo shows a finished dish
  private recipeImageIndex: number | undefined;
  // whether to apply the AI-chosen recipe image — user can opt out via the checkbox on
  // the recipe-image card. Defaults on; only matters when recipeImageDataUrl exists.
  protected useRecipeImage = true;

  /** Data URL of the AI-chosen cooked-dish photo, shown (and accept/reject-able) on the
   *  results screen. Undefined when the AI flagged none or that capture is gone. */
  protected get recipeImageDataUrl(): string | undefined {
    return this.recipeImageIndex !== undefined ? this.capturedPhotos[this.recipeImageIndex]?.dataUrl : undefined;
  }

  resultItems: ResultItem[] = [];

  // the user's remembered food picks for AI-detected ingredient names, shared
  // with the meal-photo scanner (df-bc08ed83dd29). Each scanner keeps its own
  // `_local` document.
  private readonly prefMemory = new ScannerPreferenceMemory('_local/recipe_scan_preferences', 'Recipe-scan');

  // hints under the reel (language-reactive factories)
  readonly photoHints = [
    () => LL.recipeScanner.photoHintCapture(),
    () => LL.recipeScanner.photoHintBatch(),
    () => LL.recipeScanner.photoHintImage(),
  ];

  // model selection (#3783 — unified scanner model config + sequential fallback).
  // Multi mode tries the configured models in turn with a 1-hour blacklist;
  // Fixed/All/Any pass fixedModel straight through.
  // Non-private: the template reads it to render the shared model picker.
  readonly modelSelection = new ScannerModelSelection();
  private multiModelFallback = new MultiModelFallbackManager();

  // what the shared capture animations need from this page. A getter, not a
  // field: the injected animationController is not assigned yet while field
  // initialisers run.
  private get animationHooks(): ScannerPageAnimationHooks {
    return {
      animationController: this.animationController,
      cameraElement: () => this.cameraView?.getCameraElement() ?? null,
      reelSlotElements: () => this.reel?.slotElements ?? [],
      collagePhotoElements: () => this.stage?.collagePhotoElements ?? [],
      capturedPhotos: () => this.capturedPhotos,
      isViewDestroyed: () => this.viewDestroyed,
      markForCheck: () => this.cd.markForCheck(),
    };
  }
  /** Shown in the analyzing overlay while falling back to the next model. */
  retryMessage: string = null;

  // shared fake-progress driver (recipe shows a single static status line)
  private fakeProgress = new FakeScanProgress(
    [() => LL.recipeScanner.analyzing()],
    () => this.onProgressTick(),
  );

  get analyzeProgress(): number { return this.fakeProgress.progress; }

  constructor(
    private settings: SettingsService,
    private logging: LoggingService,
    private navCtrl: NavController,
    private cloudFunctions: CloudFunctionsService,
    private telemetry: TelemetryService,
    private foodDb: FoodDbService,
    private configDb: ConfigDbService,
    private debug: DebugService,
    private alertCtrl: AlertController,
    private userDb: UserDb,
    private cd: ChangeDetectorRef,
    private wakeLock: WakeLockService,
    private popoverCtrl: PopoverController,
    private popupMenu: PopupMenuProvider,
    private animationController: AnimationController,
    private onlineState: OnlineStateService,
    private stapleDb: StapleDbService,
  ) {
    super();
    // Build the device's staple copy NOW, while the page is opening, so a scan never pays for it
    // (food staples plan 4c, #5625, Requirement C6). The reads in `tryMatchFoodAsync` are then
    // answered from memory. Not awaited and nothing is reported here: the store contains and
    // reports its own failures, and a scan works without the staples exactly as it did before.
    // Caught, and REPORTED: the store contains and reports the failures it anticipates, so anything
    // arriving here is something it did not — and an empty catch would lose exactly those.
    void this.stapleDb.warmAsync().catch(error =>
      this.logging.trackSilentProblem('staple warm-up failed', { error: describeError(error) }));
  }

  get checkedCount(): number {
    return this.resultItems.filter(i => i.checked).length;
  }

  get servingsLabel(): TranslatedString {
    return LL.recipeScanner.servingsLabel({ count: this.servings ?? 0 });
  }

  /** Test-mode-only flag controlling visibility of the "Mock" debug button that injects
   *  fake scan results without calling the AI. Drives the dev-mock-bar in the camera phase. */
  protected get showMockButton(): boolean { return this.settings.testMode; }
  /** label for the dev MOCK button — built here (not via an `untranslated` pipe) because
   *  this page's module does not import that pipe. */
  protected mockButtonLabel: TranslatedString = untranslatedString('MOCK (dev only)');

  async ngAfterViewInit() {
    await this.modelSelection.initAsync(this.configDb, this.settings);
    if (this.viewDestroyed) return;
    await this.prefMemory.loadAsync(this.userDb.database, this.logging);
  }

  // the shared cs-scanner-model-picker emits the chosen key; the shared
  // selection persists it to the one setting every scanner reads (#3783)
  onModelSelected(key: string) {
    this.modelSelection.onModelSelected(key, this.settings);
    this.cd.markForCheck();
  }

  // ── Camera ──────────────────────────────────────────────────────────

  onCameraStateChange() {
    this.cameraPermissionDismissed = this.cameraView?.cameraService.cameraPermissionDismissed ?? false;
    this.cd.markForCheck();
  }

  onCameraError(result: CameraInitResult) {
    if (result === CameraInitResult.NoCameraWorked) {
      if (this.cameraView?.cameraService.permissionStatus?.state === 'granted') {
        void this.showErrorAlertAsync(LL.recipeScanner.error());
      }
    }
  }

  // ── Photo capture ─────────────────────────────────────────────────

  // haptic + shutter sound are handled by cs-scanner-shutter-bar
  async snapPhoto() {
    if (this.allPhotosTaken) return;
    const photo = await this.cameraView?.capturePhoto();
    if (!photo || this.viewDestroyed) return;
    const slotIndex = this.capturedPhotos.length;
    // replace (not mutate) the array — the OnPush reel/stage components only
    // re-render when the photos input gets a new reference
    this.capturedPhotos = [...this.capturedPhotos, photo];
    this.telemetry.trackEvent('recipe_scan_photo_taken', { source: 'camera' });
    this.cd.markForCheck();
    await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
  }

  openFilePicker() {
    if (this.allPhotosTaken) return;
    openScannerImagePicker({
      safeTimeout: (callback, ms) => this.safeTimeout(callback, ms),
      // cap at the remaining slots
      maxFiles: MAX_RECIPE_SCAN_IMAGES - this.capturedPhotos.length,
      onPicked: async photo => {
        if (this.viewDestroyed || this.allPhotosTaken) return;
        const slotIndex = this.capturedPhotos.length;
        this.capturedPhotos = [...this.capturedPhotos, photo];
        this.telemetry.trackEvent('recipe_scan_photo_taken', { source: 'upload' });
        this.cd.markForCheck();
        await animateCapturedPhotoToReelAsync(this.animationHooks, slotIndex, photo.dataUrl);
      },
    });
  }

  removePhotoAt(index: number) {
    this.capturedPhotos = this.capturedPhotos.filter((_, i) => i !== index);
    this.cd.markForCheck();
    void this.cameraView?.reinitCamera();
  }

  // the reel's "+" slot — bring the camera back so the user can snap another photo
  addPhoto() {
    if (this.allPhotosTaken) return;
    if (!this.cameraView?.isActive) void this.cameraView?.reinitCamera();
  }

  // open the captured photo full-screen; on native the camera is stopped first
  // (the preview composites outside the DOM, so an overlay would be invisible)
  async inspectPhotoAt(index: number) {
    const photo = this.capturedPhotos[index];
    if (!photo) return;
    const pauseCamera = !!this.cameraView?.isActive && this.cameraView.isNative;
    if (pauseCamera) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
      this.cd.markForCheck();
    }
    await this.popoverCtrl.present(ZoomImagePopover, { imageSrc: photo.dataUrl, fullscreen: true }, {
      targetFromElement: this.reel?.slotElements[index],
      targetToElement: document.body,
      animate: true,
    });
    if (this.viewDestroyed) return;
    if (pauseCamera) { void this.cameraView?.reinitCamera(); }
  }

  // clear every photo and return to a fresh camera (the Retake-all control)
  retakeAllPhotos() {
    this.capturedPhotos = [];
    this.cd.markForCheck();
    if (!this.cameraView?.isActive) void this.cameraView?.reinitCamera();
  }

  // from the results screen: discard the results and start over with the camera
  retakeFromResults() {
    this.resultItems = [];
    this.capturedPhotos = [];
    this.recipeName = '';
    this.servings = undefined;
    this.phase = 'camera';
    this.cd.markForCheck();
  }

  // ── Analysis ─────────────────────────────────────────────────────

  // submit the captured photos to the recipe scanner cloud function and route
  // the extracted ingredients through the match-and-render pipeline. Holds the
  // screen awake for the round-trip (release in finally).
  async analyzeRecipe() {
    if (!this.hasPhotos || this.analyzing) return;

    if (this.cameraView?.isActive) {
      await animateCollageTransitionAsync(this.animationHooks, {
        isCameraActive: !!this.cameraView?.isActive,
        stopCameraAsync: async () => { await this.cameraView?.stopCamera(); },
        cameraStopOrdering: CameraStopOrdering.BeforeCollage,
      });
    }
    if (this.viewDestroyed) return;

    this.analyzing = true;
    this.retryMessage = null;
    this.fakeProgress.start();
    this.telemetry.trackEvent('recipe_scan_start', {
      model: this.modelSelection.scanMode === ScanMode.Multi ? this.modelSelection.multiModels.join(',') : this.modelSelection.fixedModel,
      photos: this.capturedPhotos.length,
    });
    this.cd.markForCheck();

    try {
      // hold the screen awake for the cloud-functions round-trip + model
      // inference. iOS auto-lock would otherwise suspend the in-flight fetch.
      void this.wakeLock.acquireAsync('scanner:recipe');
      const result = await this.runScanAsync();
      // runScanAsync returns null only when the page went away mid-scan
      if (!result || this.viewDestroyed) return;

      // a multi-model offline failure comes back as a ProviderError result (not
      // a thrown error). The shared policy keeps the photos + offers Try Again.
      if (result.error && !this.onlineState.isLikelyOnline()) {
        this.stopAnalyzing();
        this.telemetry.trackEvent('recipe_scan_error', { error: 'network' });
        await this.handleScanFailureAsync(result.error);
        return;
      }

      // hard quota rejection (skip QualityWarning — soft warning, scan proceeds)
      if (result.quota?.rejection && result.quota.rejection !== ScanRecipeQuotaRejection.QualityWarning) {
        this.stopAnalyzing();
        await this.handleQuotaRejection(result);
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
        return;
      }

      // provider / network errors: the shared policy keeps the photos and offers
      // a Try Again for transient failures (provider error / timeout / incomplete),
      // and resets to a fresh camera for the non-retryable ones.
      if (result.error) {
        this.stopAnalyzing();
        this.telemetry.trackEvent('recipe_scan_error', { error: result.error });
        await this.handleScanFailureAsync(result.error);
        return;
      }

      // no recipe detected (blurry / not a recipe)
      if (!result.ingredients || result.ingredients.length === 0) {
        this.stopAnalyzing();
        this.telemetry.trackEvent('recipe_scan_no_recipe', { reason: result.noRecipeReason });
        const message = result.noRecipeReason === 'blurry'
          ? LL.recipeScanner.noRecipeBlurry()
          : LL.recipeScanner.noRecipeDetected();
        await this.showErrorAlertAsync(message, true);
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
        return;
      }

      // success — keep the analyzing overlay up through food matching so the
      // progress bar only completes when the results view is ready to show.
      this.telemetry.trackEvent('recipe_scan_success', { ingredientCount: result.ingredients.length, model: result.modelKey });
      this.recipeName = result.name ?? '';
      this.servings = result.servings;
      this.scannedInstructions = result.instructions ?? '';
      this.recipeImageIndex = result.recipeImageIndex;
      this.useRecipeImage = true;
      await this.matchIngredientsAsync(result.ingredients);
      if (this.viewDestroyed) return;
      this.stopAnalyzing();
      this.phase = 'results';
      this.cd.markForCheck();

    } catch (err) {
      this.stopAnalyzing();
      this.telemetry.trackEvent('recipe_scan_error', { error: 'network' });
      // a real food-search failure during matching has already been reported by the search
      // client, which throws ReportedSearchFailureError to say so. Reporting it again here
      // filed a second Sentry event for one cause. Every other failure of the scan still
      // reports exactly as before.
      //
      // This skips MORE than the duplicate event, and that was a deliberate decision
      // (owner, 2026-09-07). trackAbnormalException is called here with no userMessage, so it
      // also shows the generic "we noticed something unusual, would you like to report it?"
      // toast. For this one failure class that toast is gone. It was a second interruption
      // about a failure this page already reports in the user's own language, right below —
      // handleScanFailureAsync puts up the scan-failure screen with a Try Again. The same
      // argument settled df-cfd308fcb9a2 for the same failure one layer down.
      //
      // What is NOT lost, so nobody has to re-derive it: the failure is still reported once,
      // with the full error text, by the search client; the app-review-prompt flag is still
      // set, because trackSilentProblem routes through noteSilentProblem and the search client does
      // not mark this report SessionHealth.NotTheUsersSession; and a sentry_error Mixpanel row is still sent,
      // as kind: problem rather than kind: error. What the user loses is the one-tap button
      // that would have attached their own bug report to the Sentry event.
      if (!(err instanceof ReportedSearchFailureError)) {
        this.logging.trackAbnormalException(err);
      }
      // a thrown request error is transient; pass ProviderError as the stand-in
      // and let the shared policy keep the photos + offer Try Again (offline copy
      // when the connectivity signal says we are offline).
      await this.handleScanFailureAsync(ScanRecipeError.ProviderError);
    } finally {
      void this.wakeLock.releaseAsync('scanner:recipe');
    }
  }

  /** Dev-only (test-mode) MOCK entry point: injects a baked scan result instead of a
   *  real AI scan, so the results screen + apply round-trip can be exercised (and
   *  E2E-tested) in a browser without a camera, the backend, or provider tokens. Opens
   *  a picker over the baked recipes — see recipe-scan-mock-fixtures. */
  async mockAnalyze(event: Event) {
    if (!this.showMockButton) return;
    await this.popupMenu.createMenu({
      buttons: MOCK_SCANNED_RECIPES.map(fixture => ({
        text: untranslatedString(fixture.label),
        handler: () => { void this.injectMockRecipeAsync(fixture.recipe); },
      })),
      targetFromElement: event.currentTarget as HTMLElement,
    });
  }

  /** Inject a baked scan result where the real success path would put it, then run the
   *  SAME food matching so the matched/unmatched results UX is fully exercised. */
  private async injectMockRecipeAsync(result: ScanRecipeResults) {
    // stop the live preview if running — the mock has no photos to collage
    if (this.cameraView?.isActive) {
      await this.cameraView.stopCamera();
      if (this.viewDestroyed) return;
    }
    // show the analyzing overlay while the (real) food matching runs, mirroring the
    // success path so the progress UI is exercised too — only the AI call is skipped
    this.analyzing = true;
    this.fakeProgress.start();
    this.cd.markForCheck();

    this.recipeName = result.name ?? '';
    this.servings = result.servings;
    this.scannedInstructions = result.instructions ?? '';
    this.recipeImageIndex = result.recipeImageIndex;
    this.useRecipeImage = true;
    try {
      await this.matchIngredientsAsync(result.ingredients ?? []);
    } catch (err) {
      // a rejection from this click-handler path would otherwise vanish; surface it the
      // same way the real analyze path does
      this.stopAnalyzing();
      if (this.viewDestroyed) return;
      this.logging.trackAbnormalException(err);
      await this.showErrorAlertAsync(LL.recipeScanner.error(), true);
      return;
    }
    if (this.viewDestroyed) return;
    this.stopAnalyzing();
    this.phase = 'results';
    this.cd.markForCheck();
  }

  // Single-call or multi-model fallback (shared blacklist + shuffle + retry
  // loop). In Multi mode a provider-side failure on one model falls back to the
  // next instead of failing the whole scan. Returns null only when the page
  // went away mid-scan.
  private async runScanAsync(): Promise<ScanRecipeResults | null> {
    const imagesBase64 = this.capturedPhotos.map(p => p.base64);

    if (this.modelSelection.scanMode === ScanMode.Multi) {
      return runMultiModelScanAsync<ScanRecipeResults>({
        models: this.modelSelection.multiModels,
        manager: this.multiModelFallback,
        isOnline: () => this.onlineState.isLikelyOnlineOrRecheckAsync(),
        scanAsync: (modelKey, attempt, total) => {
          this.telemetry.trackEvent('recipe_scan_start', { model: modelKey, multi: true, attempt, total });
          console.log(`Recipe scan: trying ${modelKey} (attempt ${attempt + 1}/${total})`);
          // the server does not read `locale` for this scanner yet — the recipe text is copied
          // as written. Sent so a future framing hook can use it (#4206)
          return this.cloudFunctions.scanRecipeAsync({ imagesBase64, model: modelKey, locale: BOOT_LOCALE });
        },
        isProviderSideError: result =>
          result.error === ScanRecipeError.ProviderError || result.error === ScanRecipeError.ProviderTimeout || result.error === ScanRecipeError.IncompleteResponse,
        makeNetworkErrorResult: () => ({ error: ScanRecipeError.ProviderError }),
        isAborted: () => this.viewDestroyed,
        onRetry: () => this.showRetryFeedback(),
        onNetworkError: (modelKey, err) =>
          console.log(`Recipe scan: ${modelKey} network error:`, (err as Error)?.message),
        onProviderError: (modelKey, result) =>
          console.log(`Recipe scan: ${modelKey} returned error ${result.error}, blacklisting for 1hr`),
      });
    }

    // single-model or 'all' mode passes the configured model directly
    return this.cloudFunctions.scanRecipeAsync({ imagesBase64, model: this.modelSelection.fixedModel, locale: BOOT_LOCALE });
  }

  // show "retrying with a different model" feedback and restart the progress
  // bar from zero for the next model attempt
  private showRetryFeedback() {
    this.retryMessage = LL.recipeScanner.retryingDifferentServer();
    this.fakeProgress.start();
    this.cd.markForCheck();
  }

  private async handleQuotaRejection(result: ScanRecipeResults) {
    let message: TranslatedString;
    switch (result.quota.rejection) {
      case ScanRecipeQuotaRejection.AccountLocked:
        message = LL.recipeScanner.quotaAccountLocked();
        break;
      case ScanRecipeQuotaRejection.RateLimited:
        message = LL.recipeScanner.quotaRateLimited();
        break;
      case ScanRecipeQuotaRejection.DailyCooldown:
        message = LL.recipeScanner.quotaDailyCooldown();
        break;
      case ScanRecipeQuotaRejection.GlobalCap:
        message = LL.common.messages.scanAppCapacityReached();
        break;
      case ScanRecipeQuotaRejection.QualityWarning:
        // warning only — scan proceeded, shouldn't reach here
        return;
    }
    await this.alertCtrl.showAlertAsync({
      title: LL.recipeScanner.quotaTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
  }

  // ── Ingredient matching ───────────────────────────────────────────

  // match each AI-detected ingredient against preference memory, local DB, then Elasticsearch
  private async matchIngredientsAsync(detected: RecipeScanIngredient[]) {
    const items: ResultItem[] = [];
    let matchedCount = 0;
    let unmatchedCount = 0;

    for (const ingredient of detected) {
      const normalizedName = ingredient.name.toLowerCase().trim();
      const gramsLabel = LL.recipeScanner.estimatedGrams({ grams: ingredient.estimatedGrams });

      const matchedHeader = await this.tryMatchFoodAsync(ingredient.name, normalizedName);
      if (matchedHeader) {
        matchedCount++;
        // show the AI's original ingredient name under the matched food title when the
        // two differ. This makes a match like "chicken broth" -> "Chicken fillet" obvious,
        // and stops two different ingredients that matched the same food from looking like
        // an unexplained duplicate (the user sees the distinct AI names below each).
        const matchedDiffersFromAi = matchedHeader.name.toLowerCase().trim() !== normalizedName;
        // ingredient.name is an AI-detected food name, not catalog text; gramsLabel is already LL() output
        const subtitle = matchedDiffersFromAi ? untranslatedString(`${ingredient.name} · ${gramsLabel}`) : gramsLabel;
        const item = this.createResultItem(ingredient, subtitle, matchedHeader);
        await this.loadRowFoodAsync(item);
        items.push(item);
      } else {
        unmatchedCount++;
        // unmatched: the card title already IS the AI name, so just show the grams
        const placeholder: FoodHeader = { _id: '', name: ingredient.name, displayNutrients: [], recipe: false, status: undefined };
        items.push({ ...this.createResultItem(ingredient, gramsLabel, placeholder), matched: false, checked: false, foodId: undefined });
      }
    }

    this.resultItems = items;
    this.telemetry.trackEvent('recipe_scan_match_results', { matched: matchedCount, unmatched: unmatchedCount, total: detected.length });
  }

  // try matching a food name: the food the user picked before for it, else the best search match.
  // Shared with the meal-photo scanner, which does exactly the same thing — see
  // services/scanner-food-match.ts.
  private async tryMatchFoodAsync(name: string, normalizedName: string): Promise<FoodHeader | null> {
    return matchScannedFoodForScannerAsync({
      name,
      normalizedName,
      prefMemory: this.prefMemory,
      services: { foodDb: this.foodDb, configDb: this.configDb, debug: this.debug, stapleDb: this.stapleDb, logging: this.logging },
      connectionProblemName: 'Recipe-scan food match: connection error',
    });
  }

  private createResultItem(ingredient: RecipeScanIngredient, subtitle: TranslatedString, header: FoodHeader): ResultItem {
    return {
      rowId: mintScannedRowId(),
      aiName: ingredient.name,
      aiEstimatedGrams: ingredient.estimatedGrams,
      estimatedGrams: ingredient.estimatedGrams,
      confidence: ingredient.confidence,
      matched: true,
      checked: true,
      foodHeader: header,
      subtitle,
      foodId: header._id,
    };
  }

  // open edit ingredient page to adjust weight/serving
  onItemClick(item: ResultItem) {
    if (!item.matched || !item.foodId) return;

    const ingredient: IngredientReal = {
      type: IngredientType.Real,
      foodId: item.foodId,
      name: item.foodHeader.name,
      weight_g: item.estimatedGrams,
      // a row ticked "Weighed with peel" before reopens ticked, with the weight as typed
      ...(item.weighedWithRefuse ? { weighedWithRefuse: item.weighedWithRefuse } : {}),
    };

    void this.navCtrl.pushAsync(IEditIngredientPage, {
      ingredientIndex: -1,
      mode: EditIngredientMode.New,
      // this screen is opened for the WEIGHT of this one scanned row, and a scanned row holds
      // exactly one food, so the portion screen must not offer to split a recipe into several
      // (deferred finding df-bd5c614e6beb, the same bug df-d3f1a6b577ee fixed on the meal-photo
      // scanner). Its Split buttons used to appear here and do nothing at all.
      returnCapability: EditIngredientReturnCapability.SingleIngredientOnly,
      returnCallback: 'onIngredientEdited',
      local: {
        ingredients: [],
        pendingIngredient: ingredient,
        returnContext: { rowId: item.rowId },
      },
    });
  }

  /**
   * Callback from the portion screen: apply what the user did to the scanned row it was opened
   * on.
   *
   * Every answer that screen gives has to be either applied here or reported. Until deferred
   * finding df-bd5c614e6beb was fixed, this handler returned unless the action was `save` with
   * an ingredient, so the More menu's Remove item and the Split buttons popped the screen and
   * changed nothing, and a save that arrived with no ingredient vanished with nothing recorded.
   * Split is no longer offered on this path — the push above says so with `returnCapability` —
   * so a split arriving here would mean that suppression has come undone.
   */
  async onIngredientEdited(context: { rowId: string }, result: EditIngredientResult) {
    // no result at all is the user leaving the screen without choosing anything. That is
    // ordinary, and reporting it would bury the answers that really cannot be applied.
    if (!result) return;

    if (result.action !== 'save' && result.action !== 'delete') {
      this.logging.trackSilentProblem('Recipe-scan portion screen returned an action the scanner cannot apply', {
        action: result.action,
      });
      return;
    }
    if (result.action === 'save' && !result.ingredient) {
      this.logging.trackSilentProblem('Recipe-scan portion screen saved with no ingredient', { rowId: context.rowId });
      return;
    }

    const item = findScannedRowById(this.resultItems, context.rowId);
    if (!item) return;

    if (result.action === 'delete') {
      // "Remove" on the portion screen means "leave this food out of the recipe". The recipe is
      // built only from ticked rows, so unticking is exactly that — and the user can tick it
      // again from the list, which deleting the row outright would not allow.
      item.checked = false;
    } else {
      item.estimatedGrams = result.ingredient.weight_g;
      // the eaten weight above was converted from a weight taken with the peel if this is set (#5544)
      item.weighedWithRefuse = isIngredientReal(result.ingredient) ? result.ingredient.weighedWithRefuse : undefined;
      this.refreshRowSubtitle(item);
      this.refreshRowNutrients(item);
      item.checked = true;
    }

    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  onItemToggled(item: ResultItem, checked: boolean) {
    item.checked = checked;
    this.cd.markForCheck();
  }

  // ── Replace/Search food ───────────────────────────────────────────

  // open food search to replace a matched food or find an unmatched one
  replaceFood(item: ResultItem) {
    this.openFoodSearchAsync(item, 'onFoodReplaced');
  }

  searchFood(item: ResultItem) {
    this.openFoodSearchAsync(item, 'onFoodSearched');
  }

  private openFoodSearchAsync(item: ResultItem, returnCallback: string) {
    void this.navCtrl.pushAsync(IFoodsPage, {
      mode: 'select',
      returnCallback,
      local: { returnContext: { rowId: item.rowId } },
      // the name is what the user wants to search for, so it stays as the search text
      initialSearchText: item.aiName,
    });
  }

  async onFoodReplaced(context: { rowId: string }, result: FoodsPageResult) {
    await this.handleFoodSelectionAsync(context, result, 'recipe_scan_replaced');
  }

  async onFoodSearched(context: { rowId: string }, result: FoodsPageResult) {
    await this.handleFoodSelectionAsync(context, result);
  }

  /**
   * The row's grams line: the AI estimate alone, or the estimate and the user's own value once they
   * differ. Shown and compared in whole grams, because a weight ticked "Weighed with peel" (#5544) is
   * an eaten weight worked out from the typed one and is rarely a whole number.
   */
  private refreshRowSubtitle(item: ResultItem) {
    const measured = Math.round(item.estimatedGrams);
    // if the user changed the weight, show both the AI estimate and the user value
    if (measured !== Math.round(item.aiEstimatedGrams)) {
      item.subtitle = LL.recipeScanner.estimatedToMeasured({ estimated: item.aiEstimatedGrams, measured });
    } else {
      item.subtitle = LL.recipeScanner.estimatedGrams({ grams: measured });
    }
  }

  /**
   * Load the row's full food, so its numbers can be shown for its grams rather than per 100 g
   * (#6806). Cached, like the read a replaced food already gets below.
   */
  private async loadRowFoodAsync(item: ResultItem) {
    item.food = await this.foodDb.getFoodByIdAsync(item.foodHeader._id, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing }) ?? undefined;
    this.refreshRowNutrients(item);
  }

  // the row's calories and macros for its current grams. if the food could not be loaded, show
  // none: the header's own numbers are per 100 g and would read as the amount in the recipe
  private refreshRowNutrients(item: ResultItem) {
    item.nutrients = item.food ? scannedRowDisplayNutrients(item.food, item.estimatedGrams) : [];
  }

  // shared handler for replace/search food selection callbacks
  private async handleFoodSelectionAsync(context: { rowId: string }, result: FoodsPageResult, telemetryEvent?: string) {
    if (!result || result.action !== 'select') return;

    const food = await this.foodDb.getFoodByIdAsync(result.foodId, { cacheFood: true, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
    if (!food || this.viewDestroyed) return;

    const item = findScannedRowById(this.resultItems, context.rowId);
    if (!item) return;

    item.foodHeader = scannerFoodHeader(food);
    item.food = food;
    item.foodId = result.foodId;
    item.matched = true;
    item.checked = true;
    // if the row was ticked "Weighed with peel", its weight is the old food's eaten weight, worked out
    // with the old food's uneaten share. that share does not apply to the new food, so put back the
    // weight that was on the scale and drop the tick (#5544)
    if (item.weighedWithRefuse) {
      // working the weight back can land a hair off the number typed (43.00000000000001), so drop that noise
      item.estimatedGrams = Math.round(typedWeight(item.estimatedGrams, item.weighedWithRefuse.percent) * 1000) / 1000;
      item.weighedWithRefuse = undefined;
      this.refreshRowSubtitle(item);
    }
    this.refreshRowNutrients(item);

    await this.prefMemory.recordPickAsync(item.aiName, result.foodId, food.name, this.userDb.database, this.logging);
    if (telemetryEvent) {
      this.telemetry.trackEvent(telemetryEvent, { aiName: item.aiName, foodId: result.foodId });
    }
    if (this.viewDestroyed) return;
    this.cd.markForCheck();
  }

  // ── Use recipe (round-trip back to Create Recipe) ──────────────────

  async useRecipe() {
    const checkedItems = this.resultItems.filter(i => i.checked && i.matched && i.foodId);

    // save preferences for all checked items
    for (const item of checkedItems) {
      await this.prefMemory.recordPickAsync(item.aiName, item.foodId, item.foodHeader.name, this.userDb.database, this.logging);
    }

    const matchedIngredients: RecipeScannerMatchedIngredient[] = checkedItems.map(item => ({
      aiName: item.aiName,
      estimatedGrams: item.estimatedGrams,
      confidence: item.confidence,
      food: item.foodHeader,
      ...(item.weighedWithRefuse ? { weighedWithRefuse: item.weighedWithRefuse } : {}),
    }));

    this.telemetry.trackEvent('recipe_scan_added', { count: matchedIngredients.length });

    // stash the cooked-dish photo (if the AI flagged one and we still have that capture)
    // as the recipe image. The data URL is too large for the nav return params, so it
    // goes through the in-memory holder that onRecipeScanResult consumes. Always set
    // (to null when none) so a prior scan's image can't leak into this one.
    const imgIndex = this.recipeImageIndex;
    const dishPhoto = (this.useRecipeImage && imgIndex !== undefined) ? this.capturedPhotos[imgIndex] : undefined;
    setPendingRecipeScanImage(dishPhoto?.dataUrl ?? null);

    void this.navCtrl.popAsync({
      name: this.recipeName.trim() || undefined,
      servings: this.servings,
      instructions: this.scannedInstructions,
      matchedIngredients,
    } as RecipeScannerResult);
  }

  // ── Progress bar ──────────────────────────────────────────────────

  // end the analyzing state: fill the bar and drop the overlay
  private stopAnalyzing() {
    this.fakeProgress.stop();
    this.analyzing = false;
  }

  // per-tick sync from the shared fake-progress driver
  private onProgressTick() {
    if (this.viewDestroyed) {
      this.fakeProgress.stop();
      return;
    }
    this.cd.markForCheck();
  }

  // ── Alerts ────────────────────────────────────────────────────────

  // resetCamera: after the alert is dismissed, clear the captured photos and
  // restart the camera so an analyze error returns the user to a fresh camera
  // rather than leaving the failed photos on screen.
  private async showErrorAlertAsync(message: TranslatedString, resetCamera = false) {
    await this.alertCtrl.showAlertAsync({
      title: LL.recipeScanner.errorTitle(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
    });
    if (this.viewDestroyed) return;
    if (resetCamera) this.retakeAllPhotos();
  }

  // like showErrorAlertAsync, but for transient failures it adds the shared
  // "Try Again" button so the user can re-run the scan in place. Returns 'retry'
  // when tapped, null otherwise.
  private async showScanErrorAsync(message: TranslatedString, opts: { resetCamera?: boolean; offerRetry?: boolean } = {}): Promise<'retry' | null> {
    // without a retry button this is just the plain OK-only alert
    if (!opts.offerRetry) {
      await this.showErrorAlertAsync(message, opts.resetCamera);
      return null;
    }
    const choice = await showScanRetryAlertAsync(this.alertCtrl, LL.recipeScanner.errorTitle(), message);
    if (this.viewDestroyed) return null;
    if (opts.resetCamera) this.retakeAllPhotos();
    // zoneless + OnPush: stopAnalyzing() cleared the analyzing flag without a
    // change-detection tick, so repaint here to hide the analyzing overlay once
    // the alert is dismissed (a retry re-enters analyze which marks again).
    this.cd.markForCheck();
    return choice === 'retry' ? 'retry' : null;
  }

  // central handler for a failed recipe scan: consult the shared recovery policy,
  // show the right alert (Try Again for transient provider/network failures), and
  // re-run the scan when the user taps it. Content errors ("no recipe") keep their
  // own alert + reset. The caller must have already stopped the progress bar.
  private async handleScanFailureAsync(error: string | undefined) {
    await runScanFailureRecoveryAsync({
      error,
      isOnline: this.onlineState.isLikelyOnline(),
      showScanErrorAsync: alertOptions => this.showScanErrorAsync(LL.recipeScanner.error(), alertOptions),
      scheduleOnNextTask: run => this.safeTimeout(run, 0),
      retryScan: () => { if (!this.viewDestroyed) void this.analyzeRecipe(); },
    });
  }

  // ── Cleanup ────────────────────────────────────────────────────────

  /** cs-nav awaits ionViewCanLeave before starting the page transition. Await
   *  the native camera teardown here — the AVCapturePreviewLayer would otherwise
   *  stay visible on top of the incoming page during the route transition. */
  async ionViewCanLeave() {
    await this.cameraView?.stopCamera();
    return true;
  }

  async ionViewWillLeave() {
    this.fakeProgress.stop();
  }

  ngOnDestroy() {
    void this.cameraView?.stopCamera();
    this.fakeProgress.stop();
  }
}
